import { CheckCircleIcon, PhoneIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { Link, useMatch, useParams, useSearchParams } from 'react-router-dom';
import { RouteConstants } from '../../route-constants';
import { Button, Input, Spinner, Textarea } from '../../components/UI';
import { bookingApi, IPublicBooking, IPublicBookingProfile, isCrmError } from '../../services/crm';
import { browserTimeZone, durationLabel } from '../sales/shared/calendar.helpers';
import { BookingShell, InlineError, Steps } from './components/BookingShell.component';
import { AboutYou, AboutYouValue } from './components/AboutYou.component';
import { defaultNewsConsent } from './components/NewsOptIn.component';
import { SlotPicker } from './components/SlotPicker.component';
import { WhenWhere } from './components/WhenWhere.component';
import { attributionFromQuery, useEmbed } from './useEmbed';
import { normalizeWebsite, phoneField, phoneProblem } from '../get_started/get_started_rules';

type MeetingType = IPublicBookingProfile['meeting_types'][number];
const STEPS = ['Pick a meeting', 'Pick a time', 'Your details'];
const uuid = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16); }));
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// #region BookingPage
/**
 * /book/:slug — a rep's public booking page. Three short steps and a
 * confirmation. Nothing here needs a login; the slug is the only key.
 */
const BookingPage: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { slug: rawSlug = '' } = useParams<{ slug: string }>();
    // A team link routes to whoever on the team is free; the API lives under /team/<slug>, the page is the same.
    const isTeam = !!useMatch(RouteConstants.BOOK_TEAM);
    const slug = isTeam ? `team/${rawSlug}` : rawSlug;
    const [profile, setProfile] = React.useState<IPublicBookingProfile | null>(null);
    const [missing, setMissing] = React.useState<boolean>(false);
    const [type, setType] = React.useState<MeetingType | null>(null);
    const [timeZone, setTimeZone] = React.useState<string>(browserTimeZone());
    const [startIso, setStartIso] = React.useState<string | null>(null);
    // Arriving from the website's demo form: ?r=<submission_id>#name=…&email=… — the booking joins that request,
    // and the person's details ride in the fragment, which browsers never send to servers or in Referer headers.
    const [params] = useSearchParams();
    const submissionId = /^[0-9a-f-]{36}$/i.test(params.get('r') ?? '') ? params.get('r') : null;
    const prefill = React.useMemo(() => new URLSearchParams(window.location.hash.replace(/^#/, '')), []);
    const [name, setName] = React.useState<string>(() => (prefill.get('name') ?? '').slice(0, 120));
    const [email, setEmail] = React.useState<string>(() => (prefill.get('email') ?? '').slice(0, 254));
    const [notes, setNotes] = React.useState<string>('');
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [refreshKey, setRefreshKey] = React.useState<number>(0);
    const [booked, setBooked] = React.useState<IPublicBooking | null>(null);
    // Booking without the website enquiry: three quick questions so the rep knows who they are meeting.
    const [about, setAbout] = React.useState<AboutYouValue>(() => ({ company: '', website: '', phone: '', roles: [], products: [], marketing: defaultNewsConsent() }));
    const [rolesError, setRolesError] = React.useState<boolean>(false);
    const [websiteError, setWebsiteError] = React.useState<string | null>(null);
    const [phoneError, setPhoneError] = React.useState<string | null>(null);
    const embedded = useEmbed(params);

    React.useEffect(() => {
        document.title = 'Wallmates | Book a time';
        bookingApi.profile(slug).then((result) => {
            if (isCrmError(result)) { setMissing(true); return; }
            setProfile(result);
            if (result.meeting_types.length === 1) setType(result.meeting_types[0]);
            document.title = `Wallmates | Book with ${result.rep_name}`;
        });
    }, [slug]);

    const step = booked ? 4 : !type ? 1 : !startIso ? 2 : 3;
    const endIso = startIso && type ? new Date(Date.parse(startIso) + type.duration_min * 60_000).toISOString() : null;

    /** Lands a book-first visitor on a lead with prep before the booking is made. A failure here never blocks the booking. */
    const recordWhoTheyAre = async (website: string): Promise<string | null> => {
        const id = uuid();
        const [first, ...rest] = name.trim().split(/\s+/);
        const r = await bookingApi.bookingIntake({
            schema_version: 2, intent: 'booking', submission_id: id, first_name: first, last_name: rest.join(' '), email: email.trim(),
            company_name: about.company.trim(), website, ...phoneField(about.phone), personas: about.roles, product_interests: about.products, goals_note: notes.trim(),
            contact_consent: true, marketing_consent: about.marketing, attribution: attributionFromQuery(params)
        });
        return r === true ? id : null;
    };

    const confirm = async () => {
        if (!type || !startIso) return;
        if (!name.trim()) { setError('Tell us your name.'); return; }
        if (!EMAIL.test(email.trim())) { setError('That email address does not look right.'); return; }
        if (!submissionId && about.roles.length === 0) { setRolesError(true); setError('Tell us what describes you. One tap is enough.'); return; }
        let website = '';
        if (!submissionId) {
            const phone = phoneProblem(about.phone);
            if (phone) { setPhoneError(phone); setError(phone); return; }
            try { website = normalizeWebsite(about.website); } catch (err) { setWebsiteError((err as Error).message); setError((err as Error).message); return; }
        }
        setSubmitting(true);
        setError(null);
        const linkId = submissionId ?? await recordWhoTheyAre(website);
        const result = await bookingApi.book(slug, {
            meeting_type_id: type.id, start_at: startIso, invitee_name: name.trim(), invitee_email: email.trim(), invitee_time_zone: timeZone, notes: notes.trim() || null,
            submission_id: linkId
        });
        setSubmitting(false);
        if (isCrmError(result)) {
            if (result.status === 409) { setStartIso(null); setRefreshKey((k) => k + 1); }
            setError(result.message);
            return;
        }
        setBooked(result);
        window.scrollTo({ top: 0 });
        // When the page is embedded on the website, tell the host so it can show its own thank-you and fire conversion tags.
        // Only times and the meeting name leave the frame — never the person's details.
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'wallmates:demo_booked', submission_id: submissionId, meeting: result.title, start_at: result.start_at, end_at: result.end_at }, '*');
        }
    };

    if (missing) {
        return (
            <BookingShell>
                <div className="px-6 py-16 text-center">
                    <h1 className="text-lg font-semibold">This booking page isn&apos;t available</h1>
                    <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">Check the link you were sent, or reply to the person who shared it.</p>
                </div>
            </BookingShell>
        );
    }
    if (!profile) return <BookingShell><div className="flex justify-center px-6 py-16"><Spinner show={true} /></div></BookingShell>;

    if (booked) {
        return (
            <BookingShell>
                <div className="mx-auto max-w-lg px-6 py-10 sm:py-14">
                    <CheckCircleIcon className="h-12 w-12 text-emerald-600" />
                    <h1 className="mt-3 text-2xl font-semibold">You&apos;re booked</h1>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">A calendar invitation and a confirmation email are on their way to <span className="font-medium text-zinc-900 dark:text-zinc-100">{booked.invitee_email}</span>.</p>
                    <div className="mt-6 rounded-xl border border-surface-border bg-surface p-4">
                        <WhenWhere title={`${booked.title} with ${booked.rep_name}`} startIso={booked.start_at} endIso={booked.end_at} timeZone={timeZone} meetLink={booked.meet_link} location={type?.location} />
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                        {booked.manage_token ? <Link to={`/book/manage/${booked.manage_token}`} target={embedded ? '_blank' : undefined} rel={embedded ? 'noreferrer' : undefined} className="font-medium text-emerald-700 underline underline-offset-2 dark:text-emerald-400">Reschedule or cancel</Link> : null}
                        <button type="button" onClick={() => { setBooked(null); setStartIso(null); setRefreshKey((k) => k + 1); }} className="text-zinc-600 underline underline-offset-2 dark:text-zinc-400">Book another time</button>
                    </div>
                </div>
            </BookingShell>
        );
    }

    return (
        <BookingShell>
            <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)]">
                {/* Who and what — stays put while the right side moves through the steps. */}
                <aside className="border-b border-surface-border p-5 md:border-b-0 md:border-r">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Book a time with</div>
                    <h1 className="mt-1 text-xl font-semibold">{profile.rep_name}</h1>
                    {profile.welcome ? <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">{profile.welcome}</p> : null}
                    {type && step > 1 ? (
                        <div className="mt-5 rounded-lg border border-surface-border bg-surface p-3">
                            {startIso && endIso
                                ? <WhenWhere title={type.name} startIso={startIso} endIso={endIso} timeZone={timeZone} location={type.location} />
                                : <div className="text-sm"><div className="font-semibold">{type.name}</div><div className="text-zinc-500">{durationLabel(type.duration_min)} · {type.location === 'meet' ? 'Google Meet' : 'Phone'}</div></div>}
                            {profile.meeting_types.length > 1 ? <button type="button" onClick={() => { setType(null); setStartIso(null); }} className="mt-2 text-xs text-zinc-500 underline underline-offset-2">Change</button> : null}
                        </div>
                    ) : null}
                </aside>

                <section className="p-5 sm:p-6">
                    <div className="mb-4 flex items-center justify-between gap-3">
                        <Steps current={step} labels={STEPS} />
                        {step > 1 ? <button type="button" onClick={() => (step === 3 ? setStartIso(null) : setType(null))} className="text-xs text-zinc-500 underline underline-offset-2">Back</button> : null}
                    </div>
                    <InlineError message={step !== 3 ? error : null} />

                    {step === 1 ? (
                        <div className="flex flex-col gap-y-2">
                            {profile.meeting_types.map((t) => (
                                <button key={t.id} type="button" onClick={() => setType(t)}
                                    className="flex items-start gap-x-3 rounded-xl border border-surface-border bg-surface px-4 py-3 text-left transition hover:border-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600">
                                    {t.location === 'meet' ? <VideoCameraIcon className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" /> : <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" />}
                                    <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-x-2"><span className="font-semibold">{t.name}</span><span className="rounded-md bg-surface-muted px-1.5 py-0.5 text-xs tabular-nums text-zinc-600 dark:text-zinc-300">{durationLabel(t.duration_min)}</span></span>
                                        {t.description ? <span className="mt-0.5 block text-sm text-zinc-500 dark:text-zinc-400">{t.description}</span> : null}
                                    </span>
                                </button>
                            ))}
                            {profile.meeting_types.length === 0 ? <p className="text-sm text-zinc-500">No meetings are open for booking right now.</p> : null}
                        </div>
                    ) : null}

                    {step === 2 && type ? <SlotPicker slug={slug} typeId={type.id} timeZone={timeZone} onTimeZone={setTimeZone} onPick={setStartIso} refreshKey={refreshKey} /> : null}

                    {step === 3 ? (
                        <form className="mx-auto flex max-w-md flex-col gap-y-4" onSubmit={(ev) => { ev.preventDefault(); void confirm(); }}>
                            <p className="text-sm text-zinc-600 dark:text-zinc-400">Who should {profile.rep_name.split(' ')[0]} expect?</p>
                            <label className="flex flex-col gap-y-1 text-sm font-medium">Your name
                                <Input value={name} autoFocus autoComplete="name" placeholder="Jane Doe" onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setName(ev.target.value)} />
                            </label>
                            <label className="flex flex-col gap-y-1 text-sm font-medium">Your email
                                <Input type="email" value={email} autoComplete="email" placeholder="jane@yourbrand.com" onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setEmail(ev.target.value)} />
                                <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400">The calendar invitation, the Meet link and your confirmation go here.</span>
                            </label>
                            {!submissionId ? <AboutYou value={about} onChange={(v) => { if (v.website !== about.website) setWebsiteError(null); if (v.phone !== about.phone) setPhoneError(null); setAbout(v); if (v.roles.length) setRolesError(false); }} rolesError={rolesError} websiteError={websiteError} phoneError={phoneError} /> : null}
                            <label className="flex flex-col gap-y-1 text-sm font-medium"><span>Anything to know beforehand? <span className="font-normal text-zinc-500">(optional)</span></span>
                                <Textarea rows={3} value={notes} placeholder="What you make, what you're hoping to get out of the call, a link to your shop…" onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(ev.target.value)} />
                            </label>
                            <InlineError message={error} />
                            <Button color="emerald" disabled={submitting} className="w-full justify-center py-2.5" onClick={() => { void confirm(); }}>{submitting ? 'Booking…' : 'Confirm booking'}</Button>
                            <p className="text-center text-xs text-zinc-500 dark:text-zinc-400">By booking, you agree Wallmates may contact you about this meeting.</p>
                        </form>
                    ) : null}
                </section>
            </div>
        </BookingShell>
    );
};

export default BookingPage;
